import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { DOCUMENT_TYPES, type DocumentType } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

/**
 * Creates a person.
 *
 * Only a first and last name are required. Church data arrives incomplete — a
 * visitor card often carries nothing more — and demanding extra fields pushes
 * staff toward placeholder values, which is worse for data quality than an
 * honest blank.
 */
export function PersonForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    preferredName: '',
    documentType: '' as DocumentType | '',
    documentNumber: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    // `showModal` rather than `show`: it traps focus, renders the backdrop and
    // makes Escape close the dialog, all without a focus-trap library.
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      peopleApi.create({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        preferredName: form.preferredName.trim() || null,
        gender: 'UNSPECIFIED',
        maritalStatus: 'UNKNOWN',
        documentType: form.documentType || null,
        documentNumber: form.documentNumber.trim() || null,
        status: 'ACTIVE',
        contacts: [
          ...(form.email.trim()
            ? [{ type: 'EMAIL' as const, value: form.email.trim(), isPrimary: true }]
            : []),
          ...(form.phone.trim()
            ? [{ type: 'MOBILE' as const, value: form.phone.trim(), isPrimary: true }]
            : []),
        ],
        tagIds: [],
      }),
    onSuccess: onCreated,
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        // Field-level messages are attached to their inputs; anything else
        // becomes a form-level alert.
        const fields = error.fieldErrors;
        setFieldErrors(fields);
        if (Object.keys(fields).length === 0) {
          setFormError(t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') }));
        }
        return;
      }
      setFormError(t('errors.INTERNAL_ERROR'));
    },
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);
    mutation.mutate();
  }

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="person-form-title"
    >
      <form onSubmit={handleSubmit} noValidate className="dialog-body">
        <h2 id="person-form-title">{t('people.person.create')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <div className="form-grid">
          <Field
            label={t('people.person.firstName')}
            name="firstName"
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            error={fieldErrors.firstName}
            required
            autoFocus
          />
          <Field
            label={t('people.person.lastName')}
            name="lastName"
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            error={fieldErrors.lastName}
            required
          />
        </div>

        <Field
          label={t('people.person.preferredName')}
          hint={t('people.person.preferredNameHint')}
          name="preferredName"
          value={form.preferredName}
          onChange={(e) => setForm({ ...form, preferredName: e.target.value })}
          error={fieldErrors.preferredName}
        />

        <div className="form-grid">
          <div className="field">
            <label className="field-label" htmlFor="documentType">
              {t('common.labels.documentType')}
            </label>
            <select
              id="documentType"
              className="field-input"
              value={form.documentType}
              onChange={(e) =>
                setForm({ ...form, documentType: e.target.value as DocumentType | '' })
              }
            >
              <option value="">{t('common.labels.none')}</option>
              {DOCUMENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`people.person.documentType.${type}`)}
                </option>
              ))}
            </select>
          </div>
          <Field
            label={t('common.labels.documentNumber')}
            name="documentNumber"
            value={form.documentNumber}
            onChange={(e) => setForm({ ...form, documentNumber: e.target.value })}
            error={fieldErrors.documentNumber}
            dir="ltr"
          />
        </div>

        <div className="form-grid">
          <Field
            label={t('people.person.email')}
            name="email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            error={fieldErrors['contacts.0.value']}
            dir="ltr"
          />
          <Field
            label={t('people.person.phone')}
            name="phone"
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            dir="ltr"
          />
        </div>

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            {t('common.actions.save')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
